import { Button } from "@/components/ui/button";
import tiktokQr from "@/assets/tiktok_qr.webp";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const TIKTOK_URL = "https://www.tiktok.com/@elfaza_vibes";
const TOTAL_SLOTS = 3;

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.39a8.16 8.16 0 0 0 4.77 1.52V7.46a4.83 4.83 0 0 1-1.01-.77z" />
  </svg>
);

const fallbackVideos: DisplayPost[] = [];

function FadeInSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

type DisplayPost = { id: string; src: string; alt: string; url: string };

export const TikTokSection = () => {
  const [videos, setVideos] = useState<DisplayPost[]>(fallbackVideos);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("social_posts")
        .select("*")
        .eq("platform", "tiktok")
        .eq("is_visible", true)
        .order("display_order", { ascending: true })
        .limit(3);
      if (data && data.length > 0) {
        setVideos(data.map((p) => ({
          id: p.id,
          src: p.thumbnail_url || "",
          alt: p.caption || "Vidéo TikTok EL FAZA",
          url: p.post_url,
        })));
      }
    };
    load();
  }, []);

  const emptySlots = Math.max(0, TOTAL_SLOTS - videos.length);

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='none' stroke='%23c2410c' stroke-width='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: "60px 60px",
      }} />
      <div className="absolute bottom-1/3 left-0 w-72 h-72 rounded-full bg-accent/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <FadeInSection className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-foreground/5 border border-foreground/10 text-foreground text-sm font-body mb-6">
            <TikTokIcon className="w-3.5 h-3.5" /> @elfaza_vibes
          </span>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4 leading-tight">
            Découvre nos vidéos
            <br />
            <span className="text-gradient">TikTok</span>
          </h2>
          <p className="text-muted-foreground font-body text-base md:text-lg max-w-lg mx-auto leading-relaxed">
            Nos créations en mouvement, behind the scenes et l'énergie EL FAZA 🇹🇳🎬
          </p>
        </FadeInSection>

        {/* QR + Videos on same row */}
        <FadeInSection delay={150} className="max-w-5xl mx-auto mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 items-start">
            {/* QR */}
            <div className="flex flex-col items-center gap-2">
              <div className="aspect-[9/16] w-full bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/60 hover:shadow-lg hover:border-primary/20 transition-all duration-300 flex items-center justify-center">
                <img src={tiktokQr} alt="QR code TikTok EL FAZA" className="w-full object-contain" width="144" height="144" />
              </div>
              <p className="text-[11px] text-muted-foreground font-body">Scanne pour nous suivre 📱</p>
            </div>
            {/* Videos */}
            {videos.map((video) => (
              <button
                key={video.id}
                onClick={() => window.open(video.url, "_blank", "noopener,noreferrer")}
                className="group relative aspect-[9/16] rounded-xl overflow-hidden border border-border/50 hover:border-foreground/20 transition-all duration-300 hover:shadow-lg"
              >
                <img
                  src={video.src}
                  alt={video.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <span className="text-white text-xs font-body flex items-center gap-1.5">
                    <TikTokIcon className="w-3.5 h-3.5" /> Voir sur TikTok
                  </span>
                </div>
              </button>
            ))}
            {/* Empty slots */}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="aspect-[9/16] rounded-xl border-2 border-dashed border-border/40 bg-muted/20 flex flex-col items-center justify-center gap-2 text-muted-foreground/30"
              >
                <TikTokIcon className="w-5 h-5" />
              </div>
            ))}
          </div>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection delay={300} className="flex justify-center">
          <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-xl gap-2 px-6 border-foreground/20 text-foreground hover:bg-foreground/5 font-body">
              <TikTokIcon className="w-4 h-4" />
              Suivre sur TikTok
              <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
        </FadeInSection>
      </div>
    </section>
  );
};